/* eslint-disable import/no-duplicates */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
} from '@material-ui/core';
import TableContainer from '@material-ui/core/TableContainer';
import DateFnsUtils from '@date-io/date-fns';
import { format, parseISO } from 'date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { pt } from 'date-fns/locale';
import DeleteIcon from '@material-ui/icons/Delete';
import { toast } from 'react-toastify';

import api from '../../../services/api';
import { headerAdd } from '../../../store/modules/header/actions';
import { correctPricePtBrToEn } from '../../../utils/functions';
import { useStyles } from './DepositsAndWithdrawsStyles';
import { formatPrice } from '../../../utils/functions';

export default function DepositsAndWithdraws() {
  interface DepositAndWithdraw {
    _id: string;
    date: string;
    type: string;
    value: number | string | any;
    formattedValue: number | string;
  }

  const [depositsAndWithdraws, setDepositsAndWithdraws] = useState<
    Array<DepositAndWithdraw>
  >([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const dispatch = useDispatch();

  function request() {
    api
      .get('/depositsAndWithdraws')
      .then(res => {
        const { data } = res;
        const parsedData = data.map((item: any) => {
          return {
            ...item,
            date: format(parseISO(item.date.split('T')[0]), "dd'/'MM'/'yyyy"),
            formattedValue: formatPrice(item.value),
          };
        });
        setDepositsAndWithdraws(parsedData);
        setInitialLoading(false);
      })
      .catch(err => {
        setInitialLoading(false);
        toast.error(err.response.data.error);
      });
  }
  useEffect(() => {
    request();
  }, []);

  function handleDelete(item: DepositAndWithdraw) {
    api
      .delete('depositsAndWithdraws', {
        data: {
          _id: item._id,
        },
      })
      .then(res => {
        toast.success(res.data.message);

        setDepositsAndWithdraws(
          depositsAndWithdraws.filter(a => a._id !== item._id)
        );
        const correctValue = item.value;
        const valuePostiveOrNegative =
          item.type === 'Depósito' ? correctValue * -1 : correctValue * 1;

        dispatch(headerAdd(valuePostiveOrNegative));
      })
      .catch(err => {
        toast.error(err.response.data.error);
      });
  }

  async function handleAdd() {
    setLoading(true);
    api
      .post('depositsAndWithdraws', {
        date,
        type,
        value: correctPricePtBrToEn(value),
      })
      .then(res => {
        toast.success(res.data.message);
        const correctValue = correctPricePtBrToEn(value);
        const valuePostiveOrNegative =
          type === 'Depósito' ? correctValue * 1 : correctValue * -1;

        dispatch(headerAdd(valuePostiveOrNegative));
        request();
        setLoading(false);
      })
      .catch(err => {
        toast.error(err.response.data.error);
        setLoading(false);
      });

    setInputDate(new Date());
    setType('');
    setValue('');
  }

  const [date, setDate] = useState<string>(
    format(new Date(), "yyyy'-'MM'-'dd")
  );
  const [inputDate, setInputDate] = useState<Date | null>(new Date());
  const [type, setType] = useState('');
  const [value, setValue] = useState('');

  const handleDateChange = (selectedDate: Date | null) => {
    setInputDate(selectedDate);
    if (selectedDate != null) {
      setDate(format(selectedDate, "yyyy'-'MM'-'dd"));
    }
  };

  const classes = useStyles();
  return (
    <div className={classes.container}>
      <Typography className={classes.title}>
        Depósitos e retiradas
      </Typography>

      <div className={classes.addSection}>
        <div className={classes.addForm}>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={pt}>
            <KeyboardDatePicker
              disableToolbar
              variant="inline"
              format="dd/MM/yyyy"
              margin="normal"
              label="Data"
              value={inputDate}
              onChange={handleDateChange}
              KeyboardButtonProps={{ 'aria-label': 'change date' }}
              autoOk
              style={{ maxWidth: '150px', margin: 0 }}
            />
          </MuiPickersUtilsProvider>
          <FormControl className={classes.input}>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={type}
              onChange={(e: any) => setType(e.target.value)}
            >
              <MenuItem value="Depósito">Depósito</MenuItem>
              <MenuItem value="Retirada">Retirada</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Valor"
            placeholder="30,00"
            value={value}
            onChange={(e: any) => setValue(e.target.value)}
            style={{ maxWidth: '150px' }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">R$</InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAdd}
            disabled={loading}
            size="small"
            style={{ color: '#1E2141', fontWeight: 600, textTransform: 'none', whiteSpace: 'nowrap', padding: '4px 16px', fontSize: '13px' }}
          >
            {loading ? <CircularProgress size={22} style={{ color: '#1E2141' }} /> : 'Adicionar'}
          </Button>
        </div>
      </div>

      <TableContainer component={Paper} className={classes.paper}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell align="center">Tipo</TableCell>
              <TableCell align="center">Valor</TableCell>
              <TableCell align="right">Remover</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {initialLoading && Array.from({ length: 5 }).map((_, i) => {
              const widths = ['90px', '70px', '80px', '28px'];
              const aligns: Array<'left' | 'center' | 'right'> = ['left', 'center', 'center', 'right'];
              return (
                <TableRow key={`skeleton-${i}`}>
                  {widths.map((w, j) => (
                    <TableCell key={j} align={aligns[j]}>
                      <div style={{
                        height: j === 3 ? '24px' : '14px',
                        width: w,
                        borderRadius: j === 3 ? '50%' : '4px',
                        background: 'linear-gradient(90deg, #ffffff06 0%, #ffffff10 50%, #ffffff06 100%)',
                        backgroundSize: '400px 100%',
                        animation: 'shimmer 1.5s infinite ease-in-out',
                        display: 'inline-block',
                      }} />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {!initialLoading && depositsAndWithdraws.map(item => (
              <TableRow className={classes.row} key={item._id}>
                <TableCell component="th" scope="row">
                  {item.date}
                </TableCell>
                <TableCell align="center">
                  <span style={{
                    display: 'inline-block',
                    padding: '3px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: item.type === 'Depósito' ? '#4BDC4B18' : '#FF634718',
                    color: item.type === 'Depósito' ? '#4BDC4B' : '#FF6347',
                  }}>
                    {item.type}
                  </span>
                </TableCell>
                <TableCell align="center" style={{
                  fontWeight: 600,
                  color: item.type === 'Depósito' ? '#4BDC4B' : '#FF6347',
                }}>
                  {item.formattedValue}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleDelete(item)} style={{ color: '#ffffff44' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!initialLoading && depositsAndWithdraws.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" style={{ padding: '40px', color: '#ffffff55' }}>
                  Nenhum registro encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
